
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase/client';
import { getShops } from '@/lib/supabase/shops';

export const useOrderNumberGenerator = (shopId?: string) => {
  const [orderNumber, setOrderNumber] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const generateOrderNumber = async () => {
      if (!shopId) {
        // Si pas de boutique, utiliser un format par défaut
        const randomPart = Math.floor(Math.random() * 10000);
        setOrderNumber(`ORD-${randomPart}`);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        
        // 1. Récupérer le nom de la boutique
        const shops = await getShops();
        const shop = shops.find(s => s.id === shopId);
        const shopName = shop?.name || 'SHOP';
        const shopPrefix = shopName.toUpperCase().replace(/\s+/g, '-').substring(0, 10);
        
        // 2. Récupérer la dernière commande de cette boutique
        const { data: lastOrders, error } = await supabase
          .from('orders')
          .select('order_number')
          .eq('shop_id', shopId)
          .order('created_at', { ascending: false })
          .limit(1);
          
        if (error) {
          console.error("Erreur lors de la récupération de la dernière commande:", error);
          throw error;
        }
        
        // 3. Extraire le numéro de séquence de la dernière commande
        let nextSequence = 1;
        
        if (lastOrders && lastOrders.length > 0 && lastOrders[0].order_number) {
          const lastOrderNumber = lastOrders[0].order_number;
          const sequencePart = lastOrderNumber.split('-').pop();
          
          if (sequencePart && !isNaN(Number(sequencePart))) {
            nextSequence = Number(sequencePart) + 1;
          }
        }
        
        // 4. Générer le nouveau numéro de commande
        const newOrderNumber = `${shopPrefix}-${nextSequence.toString().padStart(4, '0')}`;
        console.log("Nouveau numéro de commande généré:", newOrderNumber);
        
        setOrderNumber(newOrderNumber);
      } catch (error) {
        console.error("Erreur lors de la génération du numéro de commande:", error);
        // Fallback en cas d'erreur
        const timestamp = new Date().getTime();
        const randomPart = Math.floor(Math.random() * 1000);
        setOrderNumber(`ORD-${timestamp}-${randomPart}`);
      } finally {
        setIsLoading(false);
      }
    };

    generateOrderNumber();
  }, [shopId]);

  return { orderNumber, isLoading };
};
